<template>
  <div class="m-0" v-if="isNumericalMission">
    <div class="text-xs-center" v-if="displayComputationWidget">
      <exo-mission-computation></exo-mission-computation>
    </div>
    <div v-if="displayResult">
      <v-row>
        <v-col class="pb-3">
          <v-row no-gutters>
            <div class="d-inline-flex ml-3 justify-center align-center">
              <span class="headline grey--text"
                >{{ currentMission.name.toUpperCase() }} - results
                comparison</span
              >
            </div>
            <v-select
              class="ml-5"
              :items="numericalMissionsToCompare"
              v-model="otherMission"
              v-on:change="changeOtherMission()"
              :error="!ephemeridesAvailable"
              label="Other mission"
            >
            </v-select>
            <v-bottom-sheet inset v-if="warningOtherMission">
              <template v-slot:activator="{ on: bottomSheet }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on: tooltip }">
                    <v-btn
                      large
                      icon
                      color="warning"
                      v-on="{ ...tooltip, ...bottomSheet }"
                    >
                      <icon
                        scale="1.5"
                        class="warning--text"
                        name="exclamation"
                      ></icon>
                    </v-btn>
                  </template>
                  <span>Alert message</span>
                </v-tooltip>
              </template>
              <v-card>
                <div class="alert-div"></div>
                <v-alert outlined :value="true" type="warning" class="m-0"
                  >{{ warningOtherMission }}
                </v-alert>
              </v-card>
            </v-bottom-sheet>
            <v-spacer></v-spacer>
            <v-bottom-sheet inset v-if="displayWarningEccentricity">
              <template v-slot:activator="{ on: bottomSheet }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on: tooltip }">
                    <v-btn
                      large
                      icon
                      color="warning"
                      v-on="{ ...tooltip, ...bottomSheet }"
                    >
                      <icon scale="1.5" name="exclamation"></icon>
                    </v-btn>
                  </template>
                  <span>Alert message</span>
                </v-tooltip>
              </template>
              <v-card>
                <div class="alert-div"></div>
                <v-alert outlined :value="true" type="warning" class="m-0">
                  {{ warningEccentricityMessage }}
                </v-alert>
              </v-card>
            </v-bottom-sheet>

            <v-tooltip bottom v-if="ephemeridesAvailable">
              <template v-slot:activator="{ on }">
                <v-btn
                  large
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="addPlotConfiguration()"
                >
                  <icon scale="1.5" name="regular/chart-bar"></icon>
                </v-btn>
              </template>
              <span>Add plot</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  large
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="dashboardTools().saveLayout(_self)"
                >
                  <icon scale="1.5" name="save"></icon>
                </v-btn>
              </template>
              <span
                >Save the layout and the configurations of the dashboard</span
              >
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  large
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="dashboardTools().resetLayout(_self)"
                >
                  <icon scale="1.5" name="undo-alt"></icon>
                </v-btn>
              </template>
              <span
                >Reset the layout and the configurations of the dashboard</span
              >
            </v-tooltip>
          </v-row>
        </v-col>
      </v-row>
      <v-divider class="mt-0"></v-divider>
      <div class="px-3">
        <numerical-comparision-plot-config
          ref="plotConfig"
          :otherResultsLog="otherResultsLog"
          v-if="currentResultsLog.results.ephemerides != null"
        ></numerical-comparision-plot-config>
        <grid-layout
          :layout.sync="gridlayout"
          :col-num="6"
          :row-height="7"
          :is-draggable="true"
          :is-resizable="true"
          :is-mirrored="false"
          :vertical-compact="true"
          :margin="[20, 10]"
          :use-css-transforms="true"
        >
          <grid-item
            :x="gridlayout[0].x"
            :y="gridlayout[0].y"
            :w="gridlayout[0].w"
            :h="gridlayout[0].h"
            :i="gridlayout[0].i"
            :minH="5"
            :minW="1"
            @resized="statsCardResized"
            @resize="statsCardResized"
          >
            <stats-card
              v-if="
                !isOrbitExtrapolationMission(currentMission) &&
                !isManeuveringStrategyDesignMission(currentMission)
              "
              color="#2A94DE"
              awesomeicon="tachometer-alt"
              title="ΔV"
              :ref="`${statsCardName}${gridlayout[0].i}`"
              :cardId="`${statsCardName}${gridlayout[0].i}`"
              :value="computeResultToDisplay(['maneuverReport', 'totalDeltaV'])"
              :unit="getResultUnitLabel(['maneuverReport', 'totalDeltaV'])"
            />
            <stats-card
              v-if="isOrbitExtrapolationMission(currentMission)"
              color="#2A94DE"
              awesomeicon="mountain"
              title="Semi-major axis change"
              :ref="`${statsCardName}${gridlayout[0].i}`"
              :cardId="`${statsCardName}${gridlayout[0].i}`"
              :value="computeResultToDisplay(['deltaSemiMajorAxis'])"
              :unit="getResultUnitLabel(['deltaSemiMajorAxis'])"
            />
            <stats-card
              v-if="isManeuveringStrategyDesignMission(currentMission)"
              color="#2A94DE"
              awesomeicon="regular/dot-circle"
              :title="
                computeResultToDisplay(['thrustModeLabel']) + ' mean power'
              "
              :ref="`${statsCardName}${gridlayout[0].i}`"
              :cardId="`${statsCardName}${gridlayout[0].i}`"
              :value="computeResultToDisplay(['thrustModeMeanPower'])"
              :unit="getResultUnitLabel(['thrustModeMeanPower'])"
            />
          </grid-item>
          <grid-item
            :x="gridlayout[1].x"
            :y="gridlayout[1].y"
            :w="gridlayout[1].w"
            :h="gridlayout[1].h"
            :i="gridlayout[1].i"
            :minH="5"
            :minW="1"
            @resized="statsCardResized"
            @resize="statsCardResized"
          >
            <stats-card
              v-if="
                !isOrbitExtrapolationMission(currentMission) &&
                !isManeuveringStrategyDesignMission(currentMission)
              "
              color="#FF7E0D"
              icon="ev_station"
              title="Used propellant"
              :ref="`${statsCardName}${gridlayout[1].i}`"
              :cardId="`${statsCardName}${gridlayout[1].i}`"
              :value="
                computeResultToDisplay(['maneuverReport', 'totalConsumption'])
              "
              :unit="getResultUnitLabel(['maneuverReport', 'totalConsumption'])"
            />
            <stats-card
              v-if="isOrbitExtrapolationMission(currentMission)"
              color="#FF7E0D"
              awesomeicon="snowboarding"
              title="Inclination change"
              :ref="`${statsCardName}${gridlayout[1].i}`"
              :cardId="`${statsCardName}${gridlayout[1].i}`"
              :value="computeResultToDisplay(['deltaInclination'])"
              :unit="getResultUnitLabel(['deltaInclination'])"
            />
            <stats-card
              v-if="isManeuveringStrategyDesignMission(currentMission)"
              color="#FF7E0D"
              awesomeicon="satellite-dish"
              :title="computeResultToDisplay(['restModeLabel']) + ' mean power'"
              :ref="`${statsCardName}${gridlayout[1].i}`"
              :cardId="`${statsCardName}${gridlayout[1].i}`"
              :value="computeResultToDisplay(['restModeMeanPower'])"
              :unit="getResultUnitLabel(['restModeMeanPower'])"
            />
          </grid-item>
          <grid-item
            :x="gridlayout[2].x"
            :y="gridlayout[2].y"
            :w="gridlayout[2].w"
            :h="gridlayout[2].h"
            :i="gridlayout[2].i"
            :minH="5"
            :minW="1"
            @resized="statsCardResized"
            @resize="statsCardResized"
          >
            <stats-card
              v-if="
                !isOrbitExtrapolationMission(currentMission) &&
                !isManeuveringStrategyDesignMission(currentMission)
              "
              color="#F72C2C"
              icon="timelapse"
              title="Thrust duration"
              :ref="`${statsCardName}${gridlayout[2].i}`"
              :cardId="`${statsCardName}${gridlayout[2].i}`"
              :value="
                computeResultToDisplay(['maneuverReport', 'totalBurnsDuration'])
              "
              :unit="
                getResultUnitLabel(['maneuverReport', 'totalBurnsDuration'])
              "
            />
            <stats-card
              v-if="isOrbitExtrapolationMission(currentMission)"
              color="#F72C2C"
              awesomeicon="history"
              title="RAAN change"
              :ref="`${statsCardName}${gridlayout[2].i}`"
              :cardId="`${statsCardName}${gridlayout[2].i}`"
              :value="computeResultToDisplay(['deltaRaan'])"
              :unit="getResultUnitLabel(['deltaRaan'])"
            />
            <stats-card
              v-if="isManeuveringStrategyDesignMission(currentMission)"
              color="#F72C2C"
              awesomeicon="battery-quarter"
              title="Battery's depth of discharge"
              :ref="`${statsCardName}${gridlayout[2].i}`"
              :cardId="`${statsCardName}${gridlayout[2].i}`"
              :value="
                computeResultToDisplay([
                  'spacecraft',
                  'batteryDefinition',
                  'depthOfDischarge'
                ])
              "
              :unit="
                getResultUnitLabel([
                  'spacecraft',
                  'batteryDefinition',
                  'depthOfDischarge'
                ])
              "
            />
          </grid-item>
          <grid-item
            :x="gridlayout[3].x"
            :y="gridlayout[3].y"
            :w="gridlayout[3].w"
            :h="gridlayout[3].h"
            :i="gridlayout[3].i"
            :minH="5"
            :minW="1"
            @resized="statsCardResized"
            @resize="statsCardResized"
          >
            <stats-card
              v-if="!isManeuveringStrategyDesignMission(currentMission)"
              color="#1d2939"
              icon="timer"
              title="Mission duration"
              :ref="`${statsCardName}${gridlayout[3].i}`"
              :cardId="`${statsCardName}${gridlayout[3].i}`"
              :value="
                computeResultToDisplay(['maneuverReport', 'simulationDuration'])
              "
              :unit="
                getResultUnitLabel(['maneuverReport', 'simulationDuration'])
              "
            />
            <stats-card
              v-if="isManeuveringStrategyDesignMission(currentMission)"
              color="#1d2939"
              awesomeicon="battery-full"
              title="Battery's nominal capacity"
              :ref="`${statsCardName}${gridlayout[3].i}`"
              :cardId="`${statsCardName}${gridlayout[3].i}`"
              :value="
                computeResultToDisplay([
                  'spacecraft',
                  'batteryDefinition',
                  'nominalCapacity'
                ])
              "
              :unit="
                getResultUnitLabel([
                  'spacecraft',
                  'batteryDefinition',
                  'nominalCapacity'
                ])
              "
            />
          </grid-item>
          <grid-item
            :x="gridlayout[4].x"
            :y="gridlayout[4].y"
            :w="gridlayout[4].w"
            :h="gridlayout[4].h"
            :i="gridlayout[4].i"
            v-if="currentMission"
            :isResizable="false"
          >
            <v-col fill-height d-flex>
              <results-card
                disableExport
                :additionalData="otherResultsLog"
                :root="_self"
              >
              </results-card>
            </v-col>
          </grid-item>
          <div v-for="(config, index) in configurations" :key="config.id">
            <grid-item
              v-if="config.gridItem"
              :x="config.gridItem.x"
              :y="config.gridItem.y"
              :w="config.gridItem.w"
              :h="config.gridItem.h"
              :i="config.gridItem.i"
              :isDraggable="config.isDraggable"
              @resize="plotCardResized"
              @resized="plotCardResized"
            >
              <v-card tile height="100%">
                <v-row wrap>
                  <v-col cols="12">
                    <v-btn
                      color="primary"
                      @click.stop="
                        dashboardTools().editPlotConfiguration(_self, index)
                      "
                      left
                      dark
                      text
                    >
                      <v-icon dark medium>edit</v-icon>
                      Edit
                    </v-btn>
                    <v-btn
                      color="primary"
                      @click.stop="
                        dashboardTools().removePlotConfiguration(
                          _self,
                          config.gridItem.i
                        )
                      "
                      left
                      dark
                      text
                    >
                      <v-icon dark medium>delete</v-icon>
                      Delete
                    </v-btn>
                  </v-col>
                </v-row>
                <plot-item
                  :gridlayout="gridlayout"
                  height="100%"
                  :root="_self"
                  :config="config"
                  :id="`${plotingAreaName}${config.gridItem.i}`"
                  :ref="`${plotingAreaName}${config.gridItem.i}`"
                  class="elevation-5"
                >
                </plot-item>
              </v-card>
            </grid-item>
          </div>
        </grid-layout>
      </div>
    </div>
  </div>
</template>

<script>
import dashboardComponents from '../../components/Dashboard/index.js'
import dataTools from '../../tools/data.js'
import dashboardTools from '../../tools/dashboard.js'
import { mapGetters } from 'vuex'
import Vue from 'vue'
import errorMixin from '../../mixins/logHandler.js'
import plotInput from '../../mixins/plotInput.js'
import plotLayout from '../../mixins/plotLayout.js'
import plotData from '../../mixins/plotData.js'
import Plotly from 'plotly.js-dist'
import { setTimeout } from 'timers'

Vue.use(dashboardComponents)

export default {
  name: 'NumericalComparison',
  mixins: [errorMixin, plotLayout, plotInput, plotData],

  data() {
    return {
      otherMission: null,
      otherResultsLog: null,
      configurations: [],
      isDrawn: [],
      drawer: false,
      plotingAreaName: 'plotingAreaNumerical',
      statsCardName: 'statsCard',
      type: 'SimulationResult',
      configuration: this.buildEmptyConfiguration(),

      gridlayout: [
        { x: 0, y: 0, w: 1, h: 6, i: '0' },
        { x: 1, y: 0, w: 1, h: 6, i: '1' },
        { x: 2, y: 0, w: 1, h: 6, i: '2' },
        { x: 3, y: 0, w: 1, h: 6, i: '3' },
        { x: 4, y: 0, w: 1, h: 22, i: '4' }
      ]
    }
  },

  mounted() {
    this.$root.$on('current-results-updated', this.currentResultsUpdatedHandler)
    this.$root.$on('create-plot', this.createPlotHandler)
  },

  beforeDestroy() {
    this.$root.$off(
      'current-results-updated',
      this.currentResultsUpdatedHandler
    )
    this.$root.$off('create-plot', this.createPlotHandler)
  },

  computed: {
    ...mapGetters({
      currentUser: 'currentUser',
      currentMission: 'currentMission',
      currentResultsLog: 'currentResultsLog',
      missions: 'missions'
    }),

    numericalMissionsToCompare() {
      return this.missions
        ?.filter(
          (mission) =>
            mission.typeName === 'SimulationRequest' &&
            mission.id !== this.currentMission?.id
        )
        .map((mission) => ({ text: mission.name, value: mission }))
    },

    displayComputationWidget() {
      return this.computationNeeded || !this.resultsLoaded
    },
    displayResult() {
      return this.resultsLoaded && !this.computationNeeded
    },
    computationNeeded() {
      return (
        this.currentMission &&
        this.currentMission.computationRequested &&
        this.currentMission.computationLogId === ''
      )
    },
    resultsLoaded() {
      return (
        this.currentResultsLog != null && this.currentResultsLog.results != null
      )
    },
    isNumericalMission() {
      return (
        this.currentMission != null &&
        this.currentMission.typeName === 'SimulationRequest'
      )
    },
    resultAvailable() {
      return (
        this.isNumericalMission &&
        this.resultsLoaded &&
        this.currentResultsLog.results ===
          Object(this.currentResultsLog.results)
      ) // check object, not array
    },
    ephemeridesAvailable() {
      return (
        this.resultAvailable &&
        this.currentResultsLog?.results?.ephemerides != null &&
        this.otherResultsLog?.results?.ephemerides != null
      )
    },
    displayWarningEccentricity() {
      return dashboardTools.displayWarningEccentricity(this)
    },
    warningEccentricityMessage() {
      return dashboardTools.warningEccentricityMessage(this)
    },
    defaultLayout() {
      let grid = [
        { x: 0, y: 0, w: 1, h: 5, i: '0' },
        { x: 1, y: 0, w: 1, h: 5, i: '1' },
        { x: 2, y: 0, w: 1, h: 5, i: '2' },
        { x: 3, y: 0, w: 1, h: 5, i: '3' },
        { x: 4, y: 0, w: 1, h: 22, i: '4' }
      ]
      return grid
    },

    warningOtherMission() {
      if (this.otherResultsLog != null) {
        if (this.otherResultsLog.results == null) {
          return 'The mission does not have any results, please compute it first.'
        }
        if (
          this.otherResultsLog?.results?.missionDateTime !==
          this.currentResultsLog?.results?.missionDateTime
        ) {
          return (
            'The 2 missions are simulated at a different date, this might impact the results : ' +
            this.currentResultsLog?.results?.missionDateTime +
            ' vs ' +
            this.otherResultsLog?.results?.missionDateTime
          )
        }
        if (this.otherMission?.dirty === true) {
          return 'You have changed some components of the mission. This results does not match the current inputs. Please recompute the mission to update.'
        }
      } else {
        return null
      }
    }
  },

  methods: {
    currentResultsUpdatedHandler() {
      this.loadLayout()
      dashboardTools.removeDuplicatedGridItem(this)
    },
    createPlotHandler(config, plotingArea, fromLayoutSave) {
      if (!fromLayoutSave && config.layout) {
        config.gridItem.h = 25
      }
      // Use timeout as workaround because nextTick doesnt work as expected
      setTimeout(() => {
        let el = this.$refs[`${this.plotingAreaName}${config.gridItem.i}`]
        if (el) {
          el = el[0]
          let parent = el ? el.$parent : undefined
          if (parent) {
            config.layout.height = parent.$el.clientHeight - 50
            if (config.data.length > 0) {
              if (
                config.data[0].x
                  .filter((data) => !isNaN(data) || typeof data === 'string')
                  .some(isNaN)
              ) {
                config.layout.xaxis.categoryorder = 'array'
                config.layout.xaxis.categoryarray = [...config.data[0].x] // to create a new array
                config.layout.xaxis.categoryarray.sort()
              }
              if (
                config.data[0].y
                  .filter((data) => !isNaN(data) || typeof data === 'string')
                  .some(isNaN)
              ) {
                config.layout.yaxis.categoryorder = 'array'
                config.layout.yaxis.categoryarray = [...config.data[0].y] // to create a new array
                config.layout.yaxis.categoryarray.sort()
              }
              Plotly.react(plotingArea, config.data, config.layout, {
                responsive: true
              })
              plotingArea.addEventListener('mouseover', (data) => {
                config.isDraggable = false
              })
              plotingArea.addEventListener('mouseleave', (data) => {
                config.isDraggable = true
              })
            }
          }
        }
      }, 100)
    },

    async changeOtherMission() {
      // load other computation log
      if (this.otherMission?.computationLogId != null) {
        try {
          let response = await this.$http.get(
            `users/${this.currentUser.id}/computationlogs/${this.otherMission.computationLogId}`
          )
          this.otherResultsLog = await response.data
        } catch (e) {
          this.displayMessage(
            'Error getting results',
            e,
            'notification',
            'error'
          )
          this.otherResultsLog = null
        }
      } else {
        this.otherResultsLog = null
      }
    },

    addDefaultAxis(axisList, condition, name) {
      if (condition) {
        let parameter = this.buidDefaultPlotParameter()
        parameter.value = name
        this.treeUpdated(parameter, 'parameter')
        axisList.push(parameter)
      }
    },

    dashboardTools() {
      return dashboardTools
    },
    loadLayout() {
      if (this.currentMission) {
        if (this.currentMission.layout) {
          let missionLayout = JSON.parse(this.currentMission.layout)
          dataTools.loadGridLayoutConfig(missionLayout, this)
        } else {
          let missionLayout = { configurations: [], layout: this.defaultLayout }
          dataTools.loadGridLayoutConfig(missionLayout, this)
        }
      }
    },

    statsCardResized(i) {
      dashboardTools.statsCardResized(this, i)
    },

    // basicPlotLoadWorkaround is a function to handle the fact that plots may spawn at the bottom of the page
    // Unfortunately, we can't resolve it the proper way until we make some cleaning in the event bus ¯\_( ͡° ͜ʖ ͡°)_/¯
    basicPlotLoadWorkaround() {
      let grid = this.gridlayout
      let newGrid = []
      grid.forEach((el) => {
        let exists = newGrid.find((elB) => {
          return elB.i === el.i
        })
        if (!exists) {
          newGrid.push(el)
        }
      })
      this.gridlayout = newGrid
      let plot0Conf = this.configurations.find(
        (el) => el.gridItem.i === 'plot0'
      )
      if (plot0Conf != null) {
        plot0Conf.gridItem = this.gridlayout.find((el) => el.i === 'plot0')
      }
    },

    plotCardResized(i, newH, newW, newHPx, newWPx) {
      let update = {
        width: newWPx,
        height: newHPx - 50
      }
      let plotingArea = document.getElementById(`${this.plotingAreaName}${i}`)
      if (plotingArea != null) {
        try {
          Plotly.relayout(plotingArea, update)
        } catch (e) {
          console.log('Error calling Plotly.relayout')
        }
      }
    },

    addPlotConfiguration() {
      dashboardTools.removeDuplicatedGridItem(this)

      let lastId =
        this.configurations.length > 0
          ? this.configurations.reduce((acc, curr) => {
              return curr.id > acc.id ? curr : acc
            }).id
          : 0
      let newId = lastId + 1
      let newConfig = this.buildEmptyConfiguration(newId)
      this.configuration = newConfig
      let biggestY = this.gridlayout.reduce((acc, curr) => {
        return acc.y > curr.y ? acc : curr
      }, this.gridlayout[0])
      let gridItem = {
        x: 0,
        y: biggestY.y + biggestY.h,
        w: 4,
        h: 25,
        i: `${'plot' + newId}`
      }
      this.configuration.gridItem = gridItem
      this.configurations.push(this.configuration)
      this.$nextTick(() => {
        this.gridlayout.push(this.configuration.gridItem)
        this.$nextTick(() => {
          let plotingArea = document.getElementById(
            `${this.plotingAreaName}${this.configuration.gridItem.i}`
          )
          if (plotingArea != null) {
            this.$root.$emit('set-plot-config', this.configuration, plotingArea)
          }
          this.$refs.plotConfig.drawer = !this.$refs.plotConfig.drawer
        })
      })
    },

    getResultUnitLabel(fieldPath) {
      return dashboardTools.getResultUnitLabel(this, fieldPath)
    },

    readValueFromPath(path, resultsLog) {
      if (resultsLog?.results != null) {
        let field = resultsLog.results
        for (let i = 0; i < path.length; i++) {
          field = field[path[i]]
        }
        return field
      } else {
        return '???'
      }
    },

    computeResultToDisplay(fieldPath) {
      let fieldType = dataTools.getFieldTypeFromPath(fieldPath, this.type, null)
      if (fieldPath.includes('ephemerides')) {
        // range shown
        return '???'
      } else {
        const refMissionValueRaw = this.readValueFromPath(
          fieldPath,
          this.currentResultsLog
        )
        const otherMissionValueRaw = this.readValueFromPath(
          fieldPath,
          this.otherResultsLog
        )

        let refMissionValue = dataTools
          .convertSi2Display(fieldType, refMissionValueRaw)
          .toString()
        let otherMissionValue = dataTools
          .convertSi2Display(fieldType, otherMissionValueRaw)
          .toString()
        refMissionValue = refMissionValue === '' ? '???' : refMissionValue
        otherMissionValue = otherMissionValue === '' ? '???' : otherMissionValue
        return refMissionValue + ' <> ' + otherMissionValue
      }
    },

    computeResultRange(resultsLog, fieldType, fieldPath) {
      if (resultsLog?.results == null) {
        return ''
      }
      let data = dataTools.convertSimulationData(
        resultsLog.results.ephemerides,
        resultsLog.results.fieldIndexes
      )
      let allVals = dataTools
        .getUniqueValues(data, fieldPath)
        .filter((value) => !isNaN(value))
      let min = dataTools.convertSi2Display(fieldType, Math.min(...allVals))
      let max = dataTools.convertSi2Display(fieldType, Math.max(...allVals))
      if (min === max) {
        return min.toString()
      } else {
        if (min.toString().includes('Infinity')) {
          return ''
        }
        let displayedString = '[' + min
        allVals.length === 2
          ? (displayedString += '; ')
          : (displayedString += ' ... ')
        return displayedString + max + ']'
      }
    },

    computeRange(fieldType, fieldPath) {
      let value = this.computeResultRange(
        this.currentResultsLog,
        fieldType,
        fieldPath
      )
      if (this.isCompareMode) {
        value =
          value +
          ' <> ' +
          this.computeResultRange(this.otherResultsLog, fieldType, fieldPath)
      }
      return value
    },

    // TODO MFI most of these methos should be deleted
    isMissionType(mission, type) {
      return (
        dashboardTools.getMissionItem(mission) &&
        dashboardTools.getMissionItem(mission).type === type
      )
    },

    isOrbitExtrapolationMission(mission) {
      return this.isMissionType(mission, 'Orbit Extrapolation')
    },

    isManeuveringStrategyDesignMission(mission) {
      return this.isMissionType(mission, 'Maneuvering Strategy Design')
    },

    mergeString(fieldPath) {
      return 'not implemented'
    },

    isStringInside(expected, fieldPath) {
      return 'not implemented'
    },

    getCurrentAPI() {
      return dashboardTools.getCurrentAPI(this)
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css?family=Roboto');
</style>
